/**
 * Student Billing Page
 * Subscription management for student users
 * $10/month CamPassed Premium
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  Crown,
  Check,
  Sparkles,
  CreditCard,
  History,
  Shield,
  Star,
  Zap,
  Trophy,
  Compass,
  Gift,
  User,
  Calendar,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Premium features with icons
const PREMIUM_FEATURES = [
  {
    key: 'full_passport_history',
    name: 'Full Passport History',
    description: 'Access your complete event attendance history, not just the last 30 days',
    icon: History
  },
  {
    key: 'advanced_rankings',
    name: 'Advanced Rankings',
    description: 'See detailed leaderboard analytics and track your ranking trends over time',
    icon: Trophy
  },
  {
    key: 'premium_discovery',
    name: 'Premium Event Discovery',
    description: 'Advanced filters, early access to exclusive events, and personalized recommendations',
    icon: Compass
  },
  {
    key: 'exclusive_perks',
    name: 'Exclusive Perks',
    description: 'VIP benefits at partner venues and exclusive discounts on campus',
    icon: Gift
  },
  {
    key: 'premium_profile',
    name: 'Premium Profile',
    description: 'Enhanced social profile with premium badges and customization options',
    icon: User
  }
];

// Current Plan Card
const CurrentPlanCard = ({ subscription, plan, onUpgrade, onCancel }) => {
  const isPremium = subscription?.is_premium;
  const periodEnd = subscription?.current_period_end 
    ? new Date(subscription.current_period_end).toLocaleDateString() 
    : null;
  
  return (
    <div className={`rounded-2xl border p-6 ${
      isPremium 
        ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent' 
        : 'border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {isPremium ? (
              <Crown className="w-5 h-5 text-amber-400" />
            ) : (
              <User className="w-5 h-5 text-white/40" />
            )}
            <span className={`text-xs font-medium uppercase tracking-wider ${
              isPremium ? 'text-amber-400' : 'text-white/40'
            }`}>
              Current Plan
            </span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-1">
            {isPremium ? 'CamPassed Premium' : 'Free Plan'}
          </h2>
          <p className="text-white/50 text-sm">
            {isPremium 
              ? `$10/month • Renews ${periodEnd || 'monthly'}` 
              : 'Basic access to CamPassed features'
            }
          </p>
        </div>
        
        <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${
          isPremium 
            ? 'bg-amber-500/20 text-amber-400' 
            : 'bg-white/10 text-white/50'
        }`}>
          {subscription?.status === 'active' ? 'Active' : 
           subscription?.status === 'trialing' ? 'Trial' :
           subscription?.status === 'past_due' ? 'Past Due' :
           subscription?.status === 'canceled' ? 'Canceled' : 'Inactive'}
        </div>
      </div>
      
      {subscription?.cancel_at_period_end && (
        <div className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
          <div className="flex items-center gap-2 text-rose-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Subscription will end on {periodEnd}</span>
          </div>
        </div>
      )}
      
      <div className="mt-6 flex gap-3">
        {isPremium ? (
          <Button
            variant="outline"
            onClick={onCancel}
            className="border-white/10 text-white/70 hover:text-rose-400 hover:border-rose-500/30"
          >
            Cancel Subscription
          </Button>
        ) : (
          <Button
            onClick={onUpgrade}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Premium
          </Button>
        )}
      </div>
    </div>
  );
};

// Premium Plan Card
const PremiumPlanCard = ({ onUpgrade, loading }) => (
  <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent p-6 relative overflow-hidden">
    {/* Decorative gradient */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-3xl" />
    
    <div className="relative">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-xl bg-amber-500/20">
          <Crown className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">CamPassed Premium</h3>
          <p className="text-amber-400/70 text-sm">Unlock the full experience</p>
        </div>
      </div>
      
      <div className="flex items-baseline gap-1 mb-6">
        <span className="text-4xl font-bold text-white">$10</span>
        <span className="text-white/50">/month</span>
      </div>
      
      <div className="space-y-3 mb-6">
        {PREMIUM_FEATURES.map(feature => (
          <div key={feature.key} className="flex items-start gap-3">
            <div className="p-1 rounded-lg bg-amber-500/20 mt-0.5">
              <Check className="w-3.5 h-3.5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">{feature.name}</p>
              <p className="text-xs text-white/40">{feature.description}</p>
            </div>
          </div>
        ))}
      </div>
      
      <Button
        onClick={onUpgrade}
        disabled={loading}
        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
      >
        {loading ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Sparkles className="w-4 h-4 mr-2" />
        )}
        Get Premium
      </Button>
      
      <p className="text-center text-xs text-white/30 mt-3">
        Cancel anytime • No commitment
      </p>
    </div>
  </div>
);

// Feature Comparison
const FeatureComparison = () => (
  <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-6">
    <h3 className="text-lg font-medium text-white mb-4">What You Get</h3>
    
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4 pb-3 border-b border-white/[0.06]">
        <div className="text-sm text-white/40">Feature</div>
        <div className="text-sm text-white/40 text-center">Free</div>
        <div className="text-sm text-amber-400 text-center">Premium</div>
      </div>
      
      {[
        { name: 'Event Discovery', free: 'Basic', premium: 'Advanced + Early Access' },
        { name: 'Passport History', free: '30 days', premium: 'Unlimited' },
        { name: 'Leaderboard Access', free: 'View Only', premium: 'Full Analytics' },
        { name: 'Profile Customization', free: 'Basic', premium: 'Premium Badges' },
        { name: 'Exclusive Perks', free: false, premium: true },
        { name: 'Priority Support', free: false, premium: true },
      ].map((item, i) => (
        <div key={i} className="grid grid-cols-3 gap-4 py-2">
          <div className="text-sm text-white">{item.name}</div>
          <div className="text-sm text-center">
            {typeof item.free === 'boolean' ? (
              item.free ? (
                <Check className="w-4 h-4 text-emerald-400 mx-auto" />
              ) : (
                <X className="w-4 h-4 text-white/20 mx-auto" />
              )
            ) : (
              <span className="text-white/50">{item.free}</span>
            )}
          </div>
          <div className="text-sm text-center">
            {typeof item.premium === 'boolean' ? (
              item.premium ? (
                <Check className="w-4 h-4 text-amber-400 mx-auto" />
              ) : (
                <X className="w-4 h-4 text-white/20 mx-auto" />
              )
            ) : (
              <span className="text-amber-400">{item.premium}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Billing History
const BillingHistory = ({ history }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-transparent p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium text-white flex items-center gap-2">
        <History className="w-5 h-5 text-white/40" />
        Billing History
      </h3>
    </div>
    
    {history && history.length > 0 ? (
      <div className="space-y-3">
        {history.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
            <div>
              <p className="text-sm text-white">{item.description}</p>
              <p className="text-xs text-white/40">{new Date(item.date).toLocaleDateString()}</p>
            </div>
            <span className="text-sm font-medium text-white">${(item.amount / 100).toFixed(2)}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-8">
        <CreditCard className="w-10 h-10 text-white/10 mx-auto mb-3" />
        <p className="text-white/40 text-sm">No billing history yet</p>
        <p className="text-white/30 text-xs mt-1">Your transactions will appear here</p>
      </div>
    )}
  </div>
);

export default function StudentBilling() {
  const { session, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  const fetchSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/student/subscription/status`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscription(data.subscription);
        setPlan(data.plan);
      }
      
      // Fetch billing history
      const historyRes = await fetch(`${API_URL}/api/student/subscription/billing-history`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setBillingHistory(historyData.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);
  
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);
  
  const handleUpgrade = async () => {
    if (!session?.access_token) return;
    
    setUpgradeLoading(true);
    try {
      // For MVP, use mock upgrade endpoint
      const res = await fetch(`${API_URL}/api/student/subscription/mock-upgrade`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          // Refresh subscription data
          await fetchSubscription();
        }
      }
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setUpgradeLoading(false);
    }
  };
  
  const handleCancel = async () => {
    if (!session?.access_token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/student/subscription/cancel`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ at_period_end: true })
      });
      
      if (res.ok) {
        await fetchSubscription();
        setShowCancelModal(false);
      }
    } catch (error) {
      console.error('Cancel error:', error);
    }
  };
  
  return (
    <div className="min-h-screen bg-obsidian p-6" data-testid="student-billing-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white">Billing & Subscription</h1>
          <p className="text-white/40 text-sm mt-1">
            Manage your CamPassed subscription
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-white/20 animate-spin" />
          </div>
        ) : (
          <>
            {/* Current Plan */}
            <CurrentPlanCard 
              subscription={subscription}
              plan={plan}
              onUpgrade={handleUpgrade}
              onCancel={() => setShowCancelModal(true)}
            />
            
            {/* Premium Card (show if not premium) */}
            {!subscription?.is_premium && (
              <PremiumPlanCard 
                onUpgrade={handleUpgrade}
                loading={upgradeLoading}
              />
            )}
            
            {/* Feature Comparison */}
            <FeatureComparison />
            
            {/* Billing History */}
            <BillingHistory history={billingHistory} />
            
            {/* Stripe Notice */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div>
                  <p className="text-sm text-white">Secure Payments</p>
                  <p className="text-xs text-white/40 mt-1">
                    All payments are processed securely through Stripe. Your payment information is never stored on our servers.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-obsidian-light rounded-2xl border border-white/[0.08] p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-2">Cancel Subscription?</h3>
            <p className="text-white/50 text-sm mb-6">
              You'll continue to have access to Premium features until the end of your current billing period. 
              After that, your account will be downgraded to the free plan.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCancelModal(false)}
                className="flex-1 border-white/10"
              >
                Keep Premium
              </Button>
              <Button
                onClick={handleCancel}
                className="flex-1 bg-rose-500/20 text-rose-400 hover:bg-rose-500/30"
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
